import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'redux-scaffolding-ts';
import { nameof } from '../../../utils/object';
import { OrderDefinition } from 'stores/dataStore';
import { Category, NMClusterDto, NMClustersStore } from 'stores/configuration/events-n-requests/non-machine-related/clusters-store';
import { Categories } from 'stores/requests/requests-store';
import { useCallback, useEffect, useState } from 'react';
import { LocationDto } from 'stores/configuration/locations/locations-store';
import TailwindMultiSelect from 'widgets/editors/tailwind-multiselect';

interface TailwindMultipleNmrClusterEditorProps {
  value: NMClusterDto[];
  onChange?: (value: NMClusterDto[]) => void;
  nmrClusters?: NMClustersStore;
  placeholder?: string;
  clearable?: boolean;
  disabled?: boolean;
  className?: string;
  nmrClustersReceived?: string[];
  category?: Categories | Category;
  loadDataOnOpen?: boolean;
  onBlur?: (event: any) => void;
  onFocus?: (event: any) => void;
}

const TailwindMultipleNmrClusterEditor: React.FC<TailwindMultipleNmrClusterEditorProps> = ({
  value,
  onChange,
  nmrClusters,
  placeholder,
  clearable,
  disabled,
  className,
  nmrClustersReceived,
  category,
  loadDataOnOpen = false,
  onBlur,
  onFocus
}) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState<NMClusterDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const getOptionsMethod = useCallback(async () => {
    try {
      setIsLoading(true);
      let filter = [];
      const orderBy: OrderDefinition[] = [{ direction: 'Ascending', field: nameof<NMClusterDto>('name'), useProfile: false }];

      if (category)
        filter.push(
          `${nameof<NMClusterDto>('category')} eq '${
            category === ('Functional' as any) || category === ('Technical' as any) ? category : Categories[category]
          }'`
        );
      if ((nmrClustersReceived || []).length > 0) filter.push({ id: { in: { type: 'guid', value: nmrClustersReceived } } });

      const result = await nmrClusters.getAllAsync({ searchQuery: '', skip: 0, take: 100000, orderBy, filter });

      if (result) {
        setOptions(result.items || []);
      }
    } catch (error) {
      console.error('Error loading locations:', error);
      setOptions([]);
    } finally {
      setIsLoading(false);
    }
  }, [category, nmrClusters, nmrClustersReceived]);

  const handleChange = (newValue: LocationDto[]) => {
    if (onChange) {
      const selectedClusters = newValue.map(v => options.find(opt => opt.id === v.id)).filter(Boolean);
      onChange(selectedClusters);
    }
  };

  const onOpenDropDown = useCallback(() => {
    if (loadDataOnOpen && options.length === 0) {
      getOptionsMethod();
    }
  }, [loadDataOnOpen, options.length, getOptionsMethod]);

  useEffect(() => {
    if (!loadDataOnOpen) {
      getOptionsMethod();
    }
  }, [loadDataOnOpen, getOptionsMethod]);

  return (
    <div className="relative w-full">
      <TailwindMultiSelect
        options={
          isLoading
            ? [{ id: 'loading', text: t('Loading...'), disabled: true }]
            : options.length > 0
            ? options.map(cluster => ({
                id: cluster.id,
                text: cluster.name
              }))
            : [{ id: 'no-options', text: t('No options available'), disabled: true }]
        }
        value={value?.map(v => ({
          id: v.id,
          text: v.name
        }))}
        onChange={handleChange}
        placeholder={placeholder || t('Select location')}
        clearable={clearable}
        className={`w-full ${className}`}
        onBlur={onBlur}
        onFocus={onFocus}
        searchable={!isLoading}
        onOpenDropDown={onOpenDropDown}
        disabled={disabled}
      />
    </div>
  );
};

// Wire up the React component to the Redux store
export default connect(['nmrClusters', NMClustersStore])(TailwindMultipleNmrClusterEditor);
